import { useEffect, useMemo, useState } from "react";
import { api, API_BASE, apiHref } from "@/lib/api";
import { unitLabel } from "@/lib/industry";
import { Printer, ExternalLink, Pencil, Ban, Search, X, Calendar, CreditCard, Factory, History, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DocketQR } from "@/components/DocketQR";
import HistoryImportDialog from "@/components/HistoryImportDialog";
import QuickEditDocketPopover from "@/components/QuickEditDocketPopover";
import { formatLocalDateTime, formatLocalTime } from "@/lib/dates";

const STATUS_COLOR = {
  loaded: "border-white text-ink",
  en_route: "border-[#71717A] text-[#A1A1AA]",
  on_site: "border-primary-orange text-primary-orange",
  discharging: "border-primary-orange text-primary-orange",
  delivered: "border-[#3F3F46] text-[#71717A]",
  cancelled: "border-red-600 text-red-600 line-through",
};

const STATUSES = ["loaded", "en_route", "on_site", "discharging", "delivered"];

// Inline chip picker — mirrors Command board so a dispatcher can toggle extras
// (Fibre / Pump / Coloured / …) and see the price adder per m³.
function ExtrasPicker({ tags, selectedIds, onChange }) {
  if (!tags || tags.length === 0) {
    return <div className="text-[10px] text-muted-industrial mono uppercase tracking-widest border border-dashed border-industrial p-2">No extras defined — add them in Settings → Extras.</div>;
  }
  const toggle = (id) => {
    const s = new Set(selectedIds || []);
    if (s.has(id)) s.delete(id); else s.add(id);
    onChange(Array.from(s));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="docket-extras-picker">
      {tags.map((t) => {
        const on = (selectedIds || []).includes(t.id);
        return (
          <button
            key={t.id}
            type="button"
            data-testid={`docket-extra-${t.name.toLowerCase()}`}
            onClick={() => toggle(t.id)}
            className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${on ? "font-bold" : "opacity-60 hover:opacity-100"}`}
            style={{
              borderColor: t.color,
              color: on ? "#ffffff" : t.color,
              background: on ? t.color : "transparent",
            }}
            title={t.price_per_m3 > 0 ? `+$${t.price_per_m3.toFixed(2)}/m³` : ""}
          >
            {on ? "✓ " : ""}{t.name}
            {t.price_per_m3 > 0 && <span className="ml-1 opacity-70">+${Math.round(t.price_per_m3)}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Read-only chip strip for the table row
function ExtrasChips({ tags, ids }) {
  if (!ids || ids.length === 0) return <span className="text-[10px] text-muted-industrial mono">—</span>;
  const byId = Object.fromEntries((tags || []).map((t) => [t.id, t]));
  return (
    <div className="flex flex-wrap gap-1">
      {ids.map((id) => {
        const t = byId[id];
        if (!t) return null;
        return (
          <span
            key={id}
            className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
            style={{ borderColor: t.color, color: t.color }}
          >
            {t.name}
          </span>
        );
      })}
    </div>
  );
}

export default function Dockets() {
  const [list, setList] = useState([]);
  const [tags, setTags] = useState([]);
  const [editing, setEditing] = useState(null);
  const [quick, setQuick] = useState(null);
  const [mixes, setMixes] = useState([]);
  useEffect(() => { api.get("/mixes").then((r) => setMixes(r.data)).catch(() => {}); }, []);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [poApplyAll, setPoApplyAll] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  // v3.7 — Filter bar state. Default = today.
  const todayStr = () => new Date().toLocaleDateString("en-CA");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [q, setQ] = useState("");
  const [preset, setPreset] = useState("today");   // "today" | "7d" | "30d" | "custom"
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const applyPreset = (p) => {
    const d = new Date();
    const iso = (dt) => dt.toLocaleDateString("en-CA");
    if (p === "today") { setStartDate(iso(d)); setEndDate(iso(d)); }
    else if (p === "yesterday") { const y = new Date(d); y.setDate(y.getDate() - 1); setStartDate(iso(y)); setEndDate(iso(y)); }
    else if (p === "7d") { const s = new Date(d); s.setDate(s.getDate() - 6); setStartDate(iso(s)); setEndDate(iso(d)); }
    else if (p === "30d") { const s = new Date(d); s.setDate(s.getDate() - 29); setStartDate(iso(s)); setEndDate(iso(d)); }
    else if (p === "all") { setStartDate(""); setEndDate(""); }
    setPreset(p);
  };

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    if (q.trim()) params.set("q", q.trim());
    return api.get(`/dockets?${params.toString()}`)
      .then((r) => setList(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/extra-tags").then((r) => setTags(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Reload when the date window changes; the search box debounces 300ms.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const clearFilters = () => { setQ(""); applyPreset("today"); };

  // v3.14e · Bulk-recompute stored totals (wait_charge, small_load_charge,
  // total) for every docket in the current date window. Use this after a
  // pricing fix so Accounts sees corrected numbers on invoices and MYOB
  // export WITHOUT touching each docket individually.
  const [recomputing, setRecomputing] = useState(false);
  const recomputeTotals = async () => {
    if (!startDate) return toast.error("Pick a start date first");
    if (!window.confirm(
      `Recompute wait charge / min-charge / totals for every docket from ${startDate}` +
      (endDate && endDate !== startDate ? ` to ${endDate}` : "") +
      "?\n\nThis is safe — it only rewrites stored numbers to match the current pricing rules. PDFs re-render automatically."
    )) return;
    setRecomputing(true);
    try {
      const res = await api.post(`/dockets/recompute-totals?start_date=${startDate}${endDate ? `&end_date=${endDate}` : ""}`);
      const { scanned, updated, unchanged } = res.data;
      if (updated > 0) {
        toast.success(`Recomputed ${updated} of ${scanned} dockets · ${unchanged} unchanged`);
      } else {
        toast.info(`All ${scanned} docket(s) already match — nothing to update`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };
  const activeSummary = useMemo(() => {
    if (!startDate && !endDate) return "All time";
    if (startDate === endDate) return startDate === todayStr() ? "Today" : startDate;
    return `${startDate || "…"} → ${endDate || "…"}`;
  }, [startDate, endDate]);

  // v3.16 · Manual push to the plant computer. Silent-noop path if the
  // Settings > Batcher Integration format is set to "none".
  const [sendingBatcher, setSendingBatcher] = useState(null);
  const [printing, setPrinting] = useState(null);
  const sendToBatcher = async (d) => {
    setSendingBatcher(d.id);
    try {
      const r = await api.post(`/dockets/${d.id}/send-to-batcher`, {});
      const disp = r.data?.dispatch;
      toast.success(`Batch file ${disp?.filename || ""} generated (${disp?.format})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send to batcher");
    } finally {
      setSendingBatcher(null);
    }
  };


  const openEdit = (d) => setQuick(d);
  const openAdvanced = (d) => {
    setEditing(d);
    setForm({
      quantity_m3: d.quantity_m3 || 0,
      price_per_m3: d.price_per_m3 || 0,
      extra_tag_ids: d.extra_tag_ids || [],
      wait_minutes_chargeable: d.wait_minutes_chargeable || 0,
      wait_charge: d.wait_charge || 0,
      signed_by: d.signed_by || "",
      driver_name: d.driver_name || "",
      slump_mm: d.slump_mm || 80,
      status: d.status || "loaded",
      notes: d.notes || "",
      po_number: d.po_number || "",
      card_surcharge_applied: !!d.card_surcharge_applied,
    });
  };

  // v3.15g · Toggle the 1.1% card surcharge inline from the Dockets row.
  // Optimistic — flip the local state immediately, roll back on failure.
  const toggleCardSurcharge = async (d) => {
    const next = !d.card_surcharge_applied;
    try {
      await api.patch(`/dockets/${d.id}`, { card_surcharge_applied: next });
      toast.success(next ? `1.1% card surcharge added to ${d.docket_no}` : `Card surcharge removed from ${d.docket_no}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update surcharge");
    }
  };

  const closeEdit = () => {
    setEditing(null);
    setForm(null);
    setConfirmingCancel(false);
    setCancelReason("");
  };

  const cancelDocket = async () => {
    if (!editing) return;
    setCancelling(true);
    try {
      await api.post(`/dockets/${editing.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(`Docket ${editing.docket_no} cancelled`);
      closeEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  // Live preview so the dispatcher sees what the customer will be billed
  const previewTotal = (() => {
    if (!form) return 0;
    const qty = Number(form.quantity_m3 || 0);
    const rate = Number(form.price_per_m3 || 0);
    const extrasPerM3 = (form.extra_tag_ids || [])
      .map((id) => tags.find((t) => t.id === id))
      .filter(Boolean)
      .reduce((s, t) => s + Number(t.price_per_m3 || 0), 0);
    return qty * rate + qty * extrasPerM3 + Number(form.wait_charge || 0);
  })();

  const save = async () => {
    if (!editing || !form) return;
    let waiveReason = null;
    if (Number(form.wait_charge || 0) < Number(editing.wait_charge || 0)) {
      waiveReason = window.prompt(`Wait charge reduced from $${Number(editing.wait_charge || 0).toFixed(2)} to $${Number(form.wait_charge || 0).toFixed(2)}.\nReason (required — shown to Accounts):`, "");
      if (waiveReason === null) return;
      if (!waiveReason.trim()) return toast.error("A reason is required to remove or reduce waiting time");
    }
    setSaving(true);
    try {
      const payload = {
        quantity_m3: Number(form.quantity_m3),
        price_per_m3: Number(form.price_per_m3),
        extra_tag_ids: form.extra_tag_ids,
        wait_minutes_chargeable: Number(form.wait_minutes_chargeable || 0),
        wait_charge: Number(form.wait_charge || 0),
        ...(waiveReason ? { wait_waive_reason: waiveReason.trim() } : {}),
        signed_by: form.signed_by,
        driver_name: form.driver_name,
        slump_mm: Number(form.slump_mm),
        status: form.status,
        notes: form.notes,
        po_number: (form.po_number || "").trim().toUpperCase(),
        po_apply_to_order: poApplyAll,
        card_surcharge_applied: !!form.card_surcharge_applied,
      };
      const { data } = await api.patch(`/dockets/${editing.id}`, payload);
      const c = data?.po_cascade;
      if (c && (c.dockets > 1 || c.invoices > 0)) {
        toast.success(`Docket ${editing.docket_no} saved`, {
          description: `PO applied to ${c.dockets} load${c.dockets === 1 ? "" : "s"} on this order${c.invoices ? ` and ${c.invoices} invoice${c.invoices === 1 ? "" : "s"}` : ""}.`,
        });
      } else {
        toast.success(`Docket ${editing.docket_no} saved`);
      }
      closeEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const invoiced = editing && editing.invoice_id;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dockets-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tag">DELIVERY</div>
          <h1 className="heading text-3xl font-black uppercase">Dockets</h1>
          <div className="text-xs text-muted-industrial mt-1">Click any row to edit — extras, rate, wait time and quantity are all editable post-completion.</div>
        </div>
        <Button data-testid="import-history-btn" variant="outline" onClick={() => setHistoryOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold"><History className="w-4 h-4 mr-1 text-primary-orange" />Import History</Button>
      </div>
      <HistoryImportDialog open={historyOpen} onOpenChange={setHistoryOpen} onDone={load} />
      {quick && (
        <QuickEditDocketPopover asDialog open={!!quick} onOpenChange={(v) => { if (!v) setQuick(null); }} docket={quick} mixes={mixes} extraTags={tags}
          onSaved={() => { setQuick(null); load(); }} onAdvanced={openAdvanced} testIdSuffix={quick.docket_no} />
      )}

      {/* v3.7 — Filter bar */}
      <div className="card-industrial p-3 md:p-4" data-testid="dockets-filter-bar">
        <div className="flex flex-wrap gap-2">
          {[
            { v: "today",     label: "Today" },
            { v: "yesterday", label: "Yesterday" },
            { v: "7d",        label: "Last 7 days" },
            { v: "30d",       label: "Last 30 days" },
            { v: "all",       label: "All" },
            { v: "custom",    label: "Custom…" },
          ].map((p) => (
            <button
              key={p.v}
              type="button"
              data-testid={`docket-preset-${p.v}`}
              onClick={() => applyPreset(p.v)}
              className={`px-3 py-1.5 mono text-[10px] uppercase tracking-widest border transition-colors ${
                preset === p.v
                  ? "border-primary-orange bg-primary-orange/10 text-primary-orange font-bold"
                  : "border-industrial text-muted-industrial hover:text-ink"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-[auto_auto_1fr_auto] gap-2 items-end">
          <div>
            <div className="label-tag mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Start</div>
            <Input
              type="date"
              data-testid="docket-start-date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPreset("custom"); }}
              className="rounded-none bg-transparent border-industrial mono h-9 w-full sm:w-40"
            />
          </div>
          <div>
            <div className="label-tag mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> End</div>
            <Input
              type="date"
              data-testid="docket-end-date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPreset("custom"); }}
              className="rounded-none bg-transparent border-industrial mono h-9 w-full sm:w-40"
            />
          </div>
          <div>
            <div className="label-tag mb-1 flex items-center gap-1"><Search className="w-3 h-3" /> Search</div>
            <div className="relative">
              <Input
                data-testid="docket-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Docket # or customer name…"
                className="rounded-none bg-transparent border-industrial mono h-9 pr-8"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  data-testid="docket-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-industrial hover:text-primary-orange"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            data-testid="docket-clear-filters"
            className="rounded-none border-industrial bg-transparent uppercase text-[10px] tracking-widest h-9"
          >
            Reset
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={recomputeTotals}
            disabled={recomputing}
            data-testid="docket-recompute-totals"
            className="rounded-none border-primary-orange text-primary-orange bg-transparent hover:bg-primary-orange/10 uppercase text-[10px] tracking-widest h-9"
            title="Recompute wait charge, min-charge and totals for every docket in the current window"
          >
            {recomputing ? "Recomputing…" : "Recompute Totals"}
          </Button>
        </div>
        <div className="mt-3 flex items-center justify-between mono text-[10px] uppercase tracking-widest text-muted-industrial">
          <span>
            Range: <span className="text-primary-orange">{activeSummary}</span>
            {q && <span> · Search: <span className="text-primary-orange">"{q}"</span></span>}
          </span>
          <span data-testid="docket-count">
            {loading ? "Loading…" : `${list.length} docket${list.length === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-industrial"><tr className="text-left">
            <th className="p-3 label-tag">Docket</th>
            <th className="p-3 label-tag">Customer</th>
            <th className="p-3 label-tag">Truck</th>
            <th className="p-3 label-tag">Mix</th>
            <th className="p-3 label-tag">Extras</th>
            <th className="p-3 label-tag text-right">Qty</th>
            <th className="p-3 label-tag text-right">Wet-mix</th>
            <th className="p-3 label-tag text-right">Total</th>
            <th className="p-3 label-tag">Status</th>
            <th className="p-3 label-tag">Loaded</th>
            <th className="p-3 label-tag text-right">Actions</th>
          </tr></thead>
          <tbody>
            {list.map((d) => (
              <tr
                key={d.id}
                data-testid={`docket-row-${d.docket_no}`}
                onClick={() => openEdit(d)}
                className="border-b border-industrial hover:bg-surface-2 cursor-pointer"
              >
                <td className="p-3 mono">{d.docket_no}</td>
                <td className="p-3">{d.customer_name}</td>
                <td className="p-3 mono">{d.truck_number}</td>
                <td className="p-3 mono">{d.mix_code}</td>
                <td className="p-3"><ExtrasChips tags={tags} ids={d.extra_tag_ids} /></td>
                <td className="p-3 mono text-right">{d.quantity_m3} {unitLabel(d.unit)}</td>
                <td className="p-3 mono text-right text-xs text-muted-industrial" title="Wet-mix agitation time">
                  {d.wet_mix_seconds != null ? `${d.wet_mix_seconds}s` : "—"}
                </td>
                <td className="p-3 mono text-right" title="Invoice total incl GST + cartage + wait">
                  ${(d.invoice_total ?? d.total ?? 0).toFixed(2)}
                  {d.wait_waive_reason && (
                    <div className="mt-0.5 inline-block mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-500 text-amber-500 cursor-help" title={`Wait charge waived/reduced — ${d.wait_waive_reason}${d.wait_waive_history?.length ? ` (by ${d.wait_waive_history[d.wait_waive_history.length - 1].by})` : ""}`} data-testid={`wait-waived-${d.docket_no}`}>Wait waived</div>
                  )}
                </td>
                <td className="p-3"><span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLOR[d.status] || ""}`}>{d.status?.replace("_", " ")}</span></td>
                <td className="p-3 mono text-xs text-muted-industrial">{formatLocalDateTime(d.loaded_at)}</td>
                <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => toggleCardSurcharge(d)}
                      data-testid={`surcharge-toggle-${d.docket_no}`}
                      title={d.card_surcharge_applied ? "1.1% card surcharge applied — click to remove" : "Add 1.1% card surcharge (paying by card)"}
                      className={`inline-flex items-center gap-1 text-xs uppercase mono px-2 py-1 border transition-colors ${d.card_surcharge_applied ? "border-primary-orange bg-primary-orange/10 text-primary-orange" : "border-industrial text-muted-industrial hover:text-ink"}`}
                    >
                      <CreditCard className="w-3.5 h-3.5" /> {d.card_surcharge_applied ? "1.1%" : "Card"}
                    </button>
                    <button
                      onClick={() => openEdit(d)}
                      data-testid={`edit-docket-${d.docket_no}`}
                      className="text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase mono"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => sendToBatcher(d)}
                      disabled={sendingBatcher === d.id}
                      data-testid={`send-batcher-${d.docket_no}`}
                      title="Push this docket to the plant computer (Command Alkon / Jonel)"
                      className="text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase mono disabled:opacity-40"
                    >
                      <Factory className="w-3.5 h-3.5" /> {sendingBatcher === d.id ? "Sending…" : "Batcher"}
                    </button>
                    <DocketQR docket={d} />
                    <button
                      onClick={async () => {
                        setPrinting(d.id);
                        try { await api.post(`/dockets/${d.id}/print`); toast.success(`Docket ${d.docket_no} sent to the yard printer`); }
                        catch (e) { toast.error(e.response?.data?.detail || "Could not send to printer"); }
                        finally { setPrinting(null); }
                      }}
                      disabled={printing === d.id}
                      data-testid={`print-docket-${d.docket_no}`}
                      title="Send straight to the yard printer (no browser tab)"
                      className="text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase mono disabled:opacity-40"
                    >
                      <Printer className="w-3.5 h-3.5" /> {printing === d.id ? "Sending…" : "Print"}
                    </button>
                    <a href={apiHref(`/dockets/${d.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`pdf-docket-${d.docket_no}`} className="text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase">
                      <ExternalLink className="w-3 h-3" /> PDF
                    </a>
                    <a href={apiHref(`/dockets/${d.id}/batch-record.pdf`)} target="_blank" rel="noreferrer" data-testid={`batch-record-${d.docket_no}`} title="Weigh record — target vs actual per material" className="text-sky-400 hover:underline inline-flex items-center gap-1 text-xs uppercase">
                      <ClipboardList className="w-3 h-3" /> Batch Rec
                    </a>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan="11" className="p-6 text-center text-muted-industrial" data-testid="docket-empty">
                  {loading ? "Loading…" : (
                    <>No dockets match this filter. Try widening the date range or clearing the search.</>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-2xl max-h-[92vh] flex flex-col p-0" data-testid="edit-docket-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-industrial shrink-0">
            <DialogTitle className="heading uppercase flex items-center gap-2">
              Edit Docket <span className="mono text-primary-orange">{editing?.docket_no}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
          {form && editing && (
            <div className="space-y-5">
              <div className="text-xs mono text-muted-industrial">
                {editing.customer_name} · {editing.truck_number} · {editing.mix_code}
              </div>

              {invoiced && (
                <div className="border border-primary-orange/40 bg-orange-950/10 p-2.5 text-[11px] text-primary-orange mono uppercase tracking-widest">
                  ⚠ This docket has already been invoiced — edits change the docket only. Re-issue the invoice if you need the new numbers on it.
                </div>
              )}

              {editing.status === "cancelled" && (
                <div className="border border-red-600/50 bg-red-950/10 p-2.5 text-[11px] text-red-500 mono uppercase tracking-widest" data-testid="docket-cancelled-banner">
                  ✕ Docket cancelled{editing.cancel_reason ? ` — ${editing.cancel_reason}` : ""}{editing.cancelled_by ? ` · by ${editing.cancelled_by}` : ""}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Quantity (m³)</Label>
                  <Input
                    data-testid="edit-docket-qty"
                    type="number"
                    step="0.1"
                    value={form.quantity_m3}
                    onChange={(e) => setForm({ ...form, quantity_m3: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
                <div>
                  <Label className="label-tag">Price / m³ ($)</Label>
                  <Input
                    data-testid="edit-docket-rate"
                    type="number"
                    step="0.01"
                    value={form.price_per_m3}
                    onChange={(e) => setForm({ ...form, price_per_m3: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
                <div>
                  <Label className="label-tag">Slump (mm)</Label>
                  <Input
                    type="number"
                    value={form.slump_mm}
                    onChange={(e) => setForm({ ...form, slump_mm: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
                <div>
                  <Label className="label-tag">Status</Label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-9 rounded-none bg-transparent border border-industrial mono px-2 text-sm uppercase"
                    data-testid="edit-docket-status"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="label-tag">Driver</Label>
                  <Input
                    value={form.driver_name}
                    onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
                <div>
                  <Label className="label-tag">Signed By</Label>
                  <Input
                    value={form.signed_by}
                    onChange={(e) => setForm({ ...form, signed_by: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
              </div>

              {/* Customer PO — usually phoned through after the pour, and it
                  belongs to the whole order rather than a single load. */}
              <div className="border border-industrial p-3">
                <Label className="label-tag">Customer PO No.</Label>
                <Input
                  data-testid="edit-docket-po"
                  value={form.po_number || ""}
                  onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                  placeholder="e.g. PO-4471"
                  className="rounded-none bg-transparent border-industrial mono uppercase mt-1"
                />
                <label className="flex items-center gap-2 mt-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="edit-docket-po-apply-all"
                    checked={poApplyAll}
                    onChange={(e) => setPoApplyAll(e.target.checked)}
                    className="accent-[rgb(var(--primary))] w-4 h-4"
                  />
                  <span className="text-xs text-muted-industrial">Apply to every load on this order <span className="text-ink">and its invoice</span></span>
                </label>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mt-1.5">
                  Prints as "Customer PO No." on the delivery docket{invoiced ? " · this docket is invoiced, the change is logged" : ""}
                </div>
              </div>

              <div>
                <Label className="label-tag">Extras (adds $ per m³)</Label>
                <div className="mt-1.5">
                  <ExtrasPicker
                    tags={tags}
                    selectedIds={form.extra_tag_ids}
                    onChange={(ids) => setForm({ ...form, extra_tag_ids: ids })}
                  />
                </div>
              </div>

              {/* v3.15g · Credit-card / merchant surcharge — 1.1% on ex-GST subtotal */}
              <div>
                <label
                  className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${form.card_surcharge_applied ? "border-primary-orange bg-primary-orange/10" : "border-industrial hover:border-industrial"}`}
                  data-testid="surcharge-dialog-toggle"
                >
                  <input
                    type="checkbox"
                    checked={!!form.card_surcharge_applied}
                    onChange={(e) => setForm({ ...form, card_surcharge_applied: e.target.checked })}
                    className="w-4 h-4 accent-primary-orange"
                  />
                  <CreditCard className={`w-4 h-4 ${form.card_surcharge_applied ? "text-primary-orange" : "text-muted-industrial"}`} />
                  <div className="flex-1">
                    <div className={`mono uppercase text-xs tracking-widest font-bold ${form.card_surcharge_applied ? "text-primary-orange" : "text-ink"}`}>
                      Card surcharge · 1.1%
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-industrial mt-0.5">
                      Applied to ex-GST subtotal · GST calculated on top
                    </div>
                  </div>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Wait Minutes (billed)</Label>
                  <Input
                    type="number"
                    value={form.wait_minutes_chargeable}
                    onChange={(e) => setForm({ ...form, wait_minutes_chargeable: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
                <div>
                  <Label className="label-tag">Wait Charge ($){editing?.wait_waive_reason ? <span className="ml-2 normal-case text-amber-500" title={editing.wait_waive_reason}>· waived: {editing.wait_waive_reason}</span> : null}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.wait_charge}
                    onChange={(e) => setForm({ ...form, wait_charge: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
              </div>

              <div className="border-t border-industrial pt-3 flex items-center justify-between">
                <div className="label-tag">Live Total</div>
                <div className="heading text-2xl font-black text-primary-orange" data-testid="edit-docket-preview-total">
                  ${previewTotal.toFixed(2)}
                </div>
              </div>
            </div>
          )}
          {form && editing && confirmingCancel && (
            <div className="border border-red-600/60 bg-red-950/10 p-3 space-y-3" data-testid="cancel-docket-confirm">
              <div className="text-xs mono uppercase tracking-widest text-red-500 font-bold flex items-center gap-2">
                <Ban className="w-3.5 h-3.5" /> Cancel this docket?
              </div>
              <div className="text-[11px] text-muted-industrial">
                {editing.status === "delivered"
                  ? "Docket is marked delivered — cancelling will VOID this delivery. Materials already deducted from inventory won't be restored automatically."
                  : "Truck will be returned to the yard and detached from the job. No inventory changes are made."}
                {" "}
                This docket will be excluded from all future invoices.
              </div>
              <div>
                <Label className="label-tag" htmlFor="cancel-reason">Reason (optional)</Label>
                <Input
                  id="cancel-reason"
                  data-testid="cancel-docket-reason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Wrong mix loaded, customer refused delivery, plant fault"
                  className="rounded-none bg-transparent border-industrial mono"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={cancelDocket}
                  disabled={cancelling}
                  data-testid="cancel-docket-confirm-btn"
                  className="rounded-none bg-red-600 hover:bg-red-700 text-white uppercase tracking-widest text-xs font-bold"
                >
                  {cancelling ? "Cancelling…" : "Confirm cancel"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setConfirmingCancel(false); setCancelReason(""); }}
                  className="rounded-none border-industrial uppercase text-xs"
                >
                  Keep docket
                </Button>
              </div>
            </div>
          )}
          </div>
          <DialogFooter className="!justify-between gap-2 flex-wrap px-6 py-3 border-t-2 border-primary-orange bg-surface-2 shrink-0 sticky bottom-0">
            {editing?.status !== "cancelled" && !invoiced && !confirmingCancel ? (
              <Button
                variant="outline"
                onClick={() => setConfirmingCancel(true)}
                data-testid="cancel-docket-btn"
                className="rounded-none border-red-600/70 text-red-500 hover:bg-red-950/30 hover:text-red-400 uppercase tracking-widest text-xs font-bold"
              >
                <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancel Docket
              </Button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={closeEdit} className="rounded-none border-industrial uppercase text-xs">Close</Button>
              <Button
                data-testid="save-docket-btn"
                onClick={save}
                disabled={saving || editing?.status === "cancelled"}
                className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold disabled:opacity-40"
              >
                {saving ? "Saving…" : "Save Docket"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
